// COV - Certificate of Verification
// CERTIFICATE OF VERIFICATION (COV) FOR THE TRANSPORT OF PLANTED TREES WITHIN PRIVATE LAND, NON-TIMBER FOREST PRODUCTS EXCEPT RATTAN AND BAMBOO
// COV is a document to be presented when transporting planted trees within private lands not registered under the Private Tree Plantation Registration and/or non-premium trees, non-timber forest products (except rattan and bamboo)

import {
   AlertDialog,
   AlertDialogAction,
   AlertDialogCancel,
   AlertDialogContent,
   AlertDialogDescription,
   AlertDialogFooter,
   AlertDialogHeader,
   AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Modal from '@/components/ui/modal';
import '@/components/ui/styles/customScrollBar.css';
import { Textarea } from '@/components/ui/textarea';
import { formatLabel, formatReviewValue, UploadCard } from '@/pages/user/ApplicationForms/CSAWForm/CSAWFormUtils';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { AlertCircle, Loader2 } from "lucide-react";
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
// import 'react-toastify/dist/ReactToastify.css';
import FormStepIndicator from '../FormStepIndicator';
import { cn } from "@/lib/utils";

const CREATE_COV_PERMIT = gql`
   mutation CreateCOVPermit($input: COVPermitInput!) {
      createCOVPermit(input: $input) {
         id
         applicationNumber
         status
         dateOfSubmission
         files {
            letterOfIntent { filename contentType }
            tallySheet { filename contentType }
            forestCertification { filename contentType }
            orCr { filename contentType }
            driverLicense { filename contentType }
            specialPowerOfAttorney { filename contentType }
         }
      }
   }
`;

const SAVE_COV_PERMIT_DRAFT = gql`
   mutation SaveCOVPermitDraft($input: COVPermitInput!) {
      saveCOVPermitDraft(input: $input) {
         id
         applicationNumber
         status
         dateOfSubmission
         files {
            letterOfIntent { filename contentType }
            tallySheet { filename contentType }
            forestCertification { filename contentType }
            orCr { filename contentType }
            driverLicense { filename contentType }
            specialPowerOfAttorney { filename contentType }
         }
      }
   }
`;

const VERIFY_PLTP_PERMIT = gql`
   query VerifyPLTPPermit($applicationNumber: String!) {
      verifyPLTPPermit(applicationNumber: $applicationNumber) {
         id
         applicationNumber
         status
         currentStage
         name
         address
      }
   }
`;

const COVForm = () => {
   const navigate = useNavigate();
   const [currentStep, setCurrentStep] = useState(() => {
      const savedStep = localStorage.getItem('covFormStep');
      return savedStep ? parseInt(savedStep, 10) : 0;
   });
   const [formData, setFormData] = useState(() => {
      const savedFormData = localStorage.getItem('covFormData');
      return savedFormData ? JSON.parse(savedFormData) : {
         applicationType: 'Certificate of Verification',
         name: '',
         address: '',
         cellphone: '',
         purpose: '',
         driverName: '',
         driverAddress: '',
         driverLicenseNumber: '',
         vehiclePlateNumber: '',
         originAddress: '',
         destinationAddress: '',
         files: {
            letterOfIntent: [],
            tallySheet: [],
            forestCertification: [],
            orCr: [],
            driverLicense: [],
            specialPowerOfAttorney: []
         },
         dateOfSubmission: '',
         status: '',
      };
   });
   const [modalOpen, setModalOpen] = useState(false);
   const [modalContent, setModalContent] = useState({ title: '', message: '' });
   const [createCOVPermit] = useMutation(CREATE_COV_PERMIT);
   const [saveCOVPermitDraft] = useMutation(SAVE_COV_PERMIT_DRAFT);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [isSavingDraft, setIsSavingDraft] = useState(false);
   const [showSubmitDialog, setShowSubmitDialog] = useState(false);
   const [showDraftDialog, setShowDraftDialog] = useState(false);
   const [pltpApplicationNumber, setPltpApplicationNumber] = useState('');
   const [isVerifyingPermit, setIsVerifyingPermit] = useState(false);
   const [verifiedPLTPData, setVerifiedPLTPData] = useState(null);
   const [verifyPLTPPermit] = useLazyQuery(VERIFY_PLTP_PERMIT, {
      onCompleted: (data) => {
         if (data?.verifyPLTPPermit) {
            setVerifiedPLTPData(data.verifyPLTPPermit);
            toast.success('PLTP permit verified successfully', {
               description: 'You can now proceed with your COV application'
            });
            // Pre-fill form data from PLTP permit
            setFormData(prev => ({
               ...prev,
               name: data.verifyPLTPPermit.name,
               address: data.verifyPLTPPermit.address
            }));
         }
         setIsVerifyingPermit(false);
      },
      onError: (error) => {
         toast.error(error.message || 'Failed to verify PLTP permit');
         setIsVerifyingPermit(false);
         setPltpApplicationNumber('');
      }
   });

   const pltpVerificationEnabled = localStorage.getItem('pltpVerificationEnabled') !== 'false';

   const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData(prev => ({ ...prev, [name]: value }));
   };

   const handleFileChange = (e, documentType) => {
      if (e.target.files) {
         const newFiles = Array.from(e.target.files);
         setFormData(prev => ({
            ...prev,
            files: {
               ...prev.files,
               [documentType]: [
                  ...(prev.files[documentType] || []),
                  ...newFiles
               ]
            }
         }));
      }
   };

   const removeFile = (documentType, fileToRemove) => {
      setFormData(prev => ({
         ...prev,
         files: {
            ...prev.files,
            [documentType]: prev.files[documentType].filter(file => file !== fileToRemove)
         }
      }));
   };

   const handleNextStep = () => {
      if (currentStep === 0 && pltpVerificationEnabled && !verifiedPLTPData) {
         toast.error('Please verify your PLTP permit first to proceed');
         return;
      }
      setCurrentStep(prev => prev + 1);
   };

   const handlePrevStep = () => {
      setCurrentStep(prev => prev - 1);
   };

   const handleSaveAsDraft = () => {
      setShowDraftDialog(true);
   };

   const handleConfirmSaveAsDraft = async () => {
      try {
         setIsSavingDraft(true);
         const input = {
            name: formData.name,
            address: formData.address,
            cellphone: formData.cellphone,
            purpose: formData.purpose,
            driverName: formData.driverName,
            driverAddress: formData.driverAddress,
            driverLicenseNumber: formData.driverLicenseNumber,
            vehiclePlateNumber: formData.vehiclePlateNumber,
            originAddress: formData.originAddress,
            destinationAddress: formData.destinationAddress,
            files: {}
         };

         // Process files
         for (const [key, files] of Object.entries(formData.files)) {
            if (files.length > 0) {
               input.files[key] = await Promise.all(files.map(async (file) => {
                  const content = await readFileAsBase64(file);
                  return {
                     filename: file.name,
                     contentType: file.type,
                     data: content
                  };
               }));
            }
         }

         const token = localStorage.getItem('token');
         const { data } = await saveCOVPermitDraft({
            variables: { input },
            context: {
               headers: {
                  'Apollo-Require-Preflight': 'true',
                  'Authorization': `Bearer ${token}`,
               },
            },
         });

         if (data.saveCOVPermitDraft) {
            setModalContent({
               title: 'Draft saved successfully!',
               message: 'Do you want to view your applications?'
            });
            setModalOpen(true);

            localStorage.removeItem('covFormStep');
            localStorage.removeItem('covFormData');
         }
      } finally {
         setIsSavingDraft(false);
         setShowDraftDialog(false);
      }
   };

   const handleVerifyPLTPPermit = async () => {
      if (!pltpApplicationNumber) {
         toast.error("Please enter a PLTP application number");
         return;
      }

      // Validate application number format
      if (!pltpApplicationNumber.match(/PMDQ-PLTP-/)) {
         toast.error("Please enter a valid PLTP application number");
         setPltpApplicationNumber('');
         return;
      }

      setIsVerifyingPermit(true);
      try {
         await verifyPLTPPermit({
            variables: { applicationNumber: pltpApplicationNumber }
         });
      } catch (error) {
         console.error('Error verifying PLTP permit:', error);
         toast.error(error.message || 'Failed to verify PLTP permit');
         setPltpApplicationNumber('');
      }
   };

   const handleSubmit = async (e) => {
      if (e) e.preventDefault();

      if (pltpVerificationEnabled && !verifiedPLTPData) {
         toast.error('Please verify your PLTP permit first');
         return;
      }

      setShowSubmitDialog(true);
   };

   const handleConfirmSubmit = async () => {
      try {
         setIsSubmitting(true);
         const input = {
            name: formData.name,
            address: formData.address,
            cellphone: formData.cellphone,
            purpose: formData.purpose,
            driverName: formData.driverName,
            driverAddress: formData.driverAddress,
            driverLicenseNumber: formData.driverLicenseNumber,
            vehiclePlateNumber: formData.vehiclePlateNumber,
            originAddress: formData.originAddress,
            destinationAddress: formData.destinationAddress,
            files: {}
         };

         // Process files
         for (const [key, files] of Object.entries(formData.files)) {
            if (files.length > 0) {
               input.files[key] = await Promise.all(files.map(async (file) => {
                  const content = await readFileAsBase64(file);
                  return {
                     filename: file.name,
                     contentType: file.type,
                     data: content
                  };
               }));
            }
         }

         const token = localStorage.getItem('token');
         const { data } = await createCOVPermit({
            variables: { input },
            context: {
               headers: {
                  'Apollo-Require-Preflight': 'true',
                  'Authorization': `Bearer ${token}`,
               },
            },
         });

         if (data.createCOVPermit) {
            setModalContent({
               title: 'Application submitted successfully!',
               message: 'Do you want to view your application?'
            });
            setModalOpen(true);
            localStorage.removeItem('covFormStep');
            localStorage.removeItem('covFormData');
         }
      } catch (error) {
         console.error('Error submitting application:', error);
         // Simplified error message for consistency with test
         toast.error("Error submitting application");
      } finally {
         setIsSubmitting(false);
         setShowSubmitDialog(false);
      }
   };

   // Make sure this helper function is defined
   const readFileAsBase64 = (file) => {
      return new Promise((resolve, reject) => {
         const reader = new FileReader();
         reader.onload = () => resolve(reader.result.split(',')[1]);
         reader.onerror = error => reject(error);
         reader.readAsDataURL(file);
      });
   };

   const renderPLTPVerificationSection = () => (
      <div className="space-y-4">
         {!pltpVerificationEnabled && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-md p-4 mb-4">
               <div className="flex items-start">
                  <div className="flex-shrink-0">
                     <svg className="h-5 w-5 text-yellow-600 dark:text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                     </svg>
                  </div>
                  <div className="ml-3">
                     <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                        PLTP Verification Disabled
                     </h3>
                     <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-400">
                        PLTP permit verification is currently disabled in developer settings. You can proceed without verifying a PLTP permit.
                     </div>
                  </div>
               </div>
            </div>
         )}

         <div className="bg-white dark:bg-gray-800 rounded-lg border p-6">
            <div className="space-y-4">
               {/* Info Section */}
               <div className="bg-blue-50 dark:bg-blue-900/20 rounded-md p-4 mb-6">
                  <div className="flex items-start">
                     <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                           <circle cx="12" cy="12" r="10" />
                           <line x1="12" y1="16" x2="12" y2="12" />
                           <line x1="12" y1="8" x2="12.01" y2="8" />
                        </svg>
                     </div>
                     <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300">
                           Before you proceed
                        </h3>
                        <div className="mt-2 text-sm text-blue-700 dark:text-blue-400">
                           <ul className="list-disc pl-5 space-y-1">
                              <li>You must have a released PLTP permit</li>
                              <li>The permit must be under your name</li>
                              <li>The permit number format should be: PMDQ-PLTP-XXXX</li>
                              <li>
                                 You can check your released permits{' '}
                                 <Link
                                    to="/applicationsStatus?tab=Applications&subtab=Released"
                                    className="text-blue-600 dark:text-blue-300 hover:underline font-medium inline-flex items-center gap-1"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                 >
                                    <span>click here to view</span>
                                    <svg
                                       className="h-4 w-4"
                                       xmlns="http://www.w3.org/2000/svg"
                                       viewBox="0 0 24 24"
                                       fill="none"
                                       stroke="currentColor"
                                       strokeWidth="2"
                                       strokeLinecap="round"
                                       strokeLinejoin="round"
                                    >
                                       <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                       <polyline points="15 3 21 3 21 9" />
                                       <line x1="10" y1="14" x2="21" y2="3" />
                                    </svg>
                                 </Link>
                              </li>
                           </ul>
                        </div>
                     </div>
                  </div>
               </div>

               {/* Input Section */}
               <div className="space-y-3">
                  <Label className="text-base font-semibold">
                     PLTP Application Number
                  </Label>
                  <div className="flex gap-3">
                     <div className="flex-1">
                        <Input
                           type="text"
                           value={pltpApplicationNumber}
                           onChange={(e) => setPltpApplicationNumber(e.target.value)}
                           placeholder="e.g., PMDQ-PLTP-2024-0001"
                           disabled={isVerifyingPermit || verifiedPLTPData}
                           required
                           className="h-11 text-base"
                        />
                     </div>
                     <Button
                        type="button"
                        variant={verifiedPLTPData ? "outline" : "default"}
                        size="lg"
                        onClick={handleVerifyPLTPPermit}
                        disabled={isVerifyingPermit || verifiedPLTPData || !pltpApplicationNumber.trim()}
                        className={`min-w-[120px] ${verifiedPLTPData
                           ? "border-green-500 text-green-600"
                           : "bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                           }`}
                     >
                        {isVerifyingPermit ? (
                           <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Verifying
                           </>
                        ) : verifiedPLTPData ? (
                           <>
                              <svg className="mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                 <path d="M20 6L9 17l-5-5" />
                              </svg>
                              Verified
                           </>
                        ) : (
                           'Verify Permit'
                        )}
                     </Button>
                  </div>
               </div>

               {/* Success Message */}
               {verifiedPLTPData && (
                  <div className="mt-4 bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
                     <div className="flex">
                        <div className="flex-shrink-0">
                           <svg className="h-5 w-5 text-green-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                              <polyline points="22 4 12 14.01 9 11.01" />
                           </svg>
                        </div>
                        <div className="ml-3">
                           <h3 className="text-sm font-medium text-green-800 dark:text-green-400">
                              Permit Verified Successfully
                           </h3>
                           <div className="mt-2 text-sm text-green-700 dark:text-green-500">
                              <p>You can now proceed with your COV application. Some details will be pre-filled based on your PLTP permit.</p>
                           </div>
                        </div>
                     </div>
                  </div>
               )}
            </div>
         </div>
      </div>
   );

   const renderApplicationDetails = () => (
      <div className="space-y-4 h-[500px] overflow-y-auto pr-4 custom-scrollbar">
         <div className="space-y-5">
            <div>
               <h3 className="text-lg font-semibold mb-2 text-green-700 dark:text-green-500">Owner Details</h3>
               <div className="space-y-4">
                  {renderApplicationDetailsInput('name', 'Name', 'Full Name')}
                  {renderApplicationDetailsInput('address', 'Address', 'Complete Address')}
                  {renderApplicationDetailsInput('cellphone', 'Phone Number', 'e.g. 09123456789')}
                  <div className="space-y-2">
                     <Label htmlFor="purpose" className="dark:text-white">Purpose</Label>
                     <div
                        className={cn(
                           validationErrors.purpose &&
                           "border-2 border-red-500 rounded-md p-1"
                        )}
                        data-error={validationErrors.purpose ? "true" : "false"}
                     >
                        <Textarea
                           id="purpose"
                           name="purpose"
                           value={formData.purpose}
                           onChange={handleInputChange}
                           placeholder="State the purpose for COV application"
                           required
                           className="min-h-[100px] dark:bg-gray-700 dark:text-white"
                        />
                        {validationErrors.purpose && (
                           <div className="flex items-center mt-1 text-sm text-red-500">
                              <AlertCircle className="h-4 w-4 mr-1" />
                              {validationErrors.purpose}
                           </div>
                        )}
                     </div>
                  </div>
               </div>
            </div>

            <div>
               <h3 className="text-lg font-semibold mb-2 text-green-700 dark:text-green-500">Driver Information</h3>
               <div className="space-y-4">
                  {renderApplicationDetailsInput('driverName', 'Driver Name', 'Full Name of Driver')}
                  {renderApplicationDetailsInput('driverAddress', 'Driver Address', 'Complete Address of Driver')}
                  {renderApplicationDetailsInput('driverLicenseNumber', 'Driver License Number', 'Enter License Number')}
                  {renderApplicationDetailsInput('vehiclePlateNumber', 'Vehicle Plate Number', 'Enter Plate Number')}
               </div>
            </div>

            <div>
               <h3 className="text-lg font-semibold mb-2 text-green-700 dark:text-green-500">Transport Details</h3>
               <div className="space-y-4">
                  {renderApplicationDetailsInput('originAddress', 'Origin Address', 'Enter Origin Address')}
                  {renderApplicationDetailsInput('destinationAddress', 'Destination Address', 'Enter Destination Address')}
               </div>
            </div>
         </div>
      </div>
   );

   const [validationErrors, setValidationErrors] = useState({});

   const validateStep = (stepNumber) => {
      // Check if validation is disabled in DevSettings
      const isValidationEnabled = localStorage.getItem('covValidationEnabled') !== 'false';
      if (!isValidationEnabled) {
         return {}; // Return empty errors object if validation is disabled
      }

      // Rest of validation remains the same...
   };
   
   const renderApplicationDetailsInput = (name, label, placeholder) => {
      return (
         <div className="space-y-2">
            <Label htmlFor={name} className="dark:text-white">{label}</Label>
            <div
               className={cn(
                  validationErrors[name] &&
                  "border-2 border-red-500 rounded-md p-1"
               )}
               data-error={validationErrors[name] ? "true" : "false"}
            >
               <Input
                  type="text"
                  id={name}
                  name={name}
                  value={formData[name]}
                  onChange={handleInputChange}
                  placeholder={placeholder}
                  required
                  className="dark:bg-gray-700 dark:text-white"
               />
               {validationErrors[name] && (
                  <div className="flex items-center mt-1 text-sm text-red-500">
                     <AlertCircle className="h-4 w-4 mr-1" />
                     {validationErrors[name]}
                  </div>
               )}
            </div>
         </div>
      );
   };

   const steps = [
      {
         title: 'PLTP Permit Verification',
         content: (
            <div className="space-y-4">
               <div className="space-y-5 h-[500px] overflow-y-auto pr-4 custom-scrollbar">
                  <div className="mb-6">
                     {/* <h3 className="text-lg font-semibold mb-2">PLTP Permit Verification</h3> */}
                     <p className="text-sm text-muted-foreground mb-4">
                        Please enter your PLTP permit application number to proceed with the COV application.
                        The permit must be released and under your name.
                     </p>
                     {renderPLTPVerificationSection()}
                  </div>
               </div>
            </div>
         )
      },
      {
         title: 'Application Details',
         content: renderApplicationDetails()
      },
      {
         title: 'Required Documents',
         content: (
            <div className="space-y-4 h-[500px] overflow-y-auto pr-4 custom-scrollbar">
               <UploadCard
                  label="Letter of Intent"
                  documentLabel="Upload Letter of Intent"
                  files={formData.files.letterOfIntent}
                  onFileChange={(e) => handleFileChange(e, 'letterOfIntent')}
                  onRemoveFile={(file) => removeFile('letterOfIntent', file)}
               />
               <input
                  type="file"
                  data-testid="letter-of-intent-input"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  className="hidden"
               />
               <UploadCard
                  label="Tally Sheet"
                  documentLabel="Upload Tally Sheet"
                  files={formData.files.tallySheet}
                  onFileChange={(e) => handleFileChange(e, 'tallySheet')}
                  onRemoveFile={(file) => removeFile('tallySheet', file)}
               />
               <UploadCard
                  label="Forest Certification"
                  documentLabel={<>Certification that the forest products are harvested within the area of the owner <strong>(For Non Timber)</strong></>}
                  files={formData.files.forestCertification}
                  onFileChange={(e) => handleFileChange(e, 'forestCertification')}
                  onRemoveFile={(file) => removeFile('forestCertification', file)}
               />
            </div>
         )
      },
      {
         title: 'Additional Documents',
         content: (
            <div className="space-y-4 h-[500px] overflow-y-auto pr-4 custom-scrollbar">
               <UploadCard
                  label="OR/CR"
                  documentLabel="Upload OR/CR"
                  files={formData.files.orCr}
                  onFileChange={(e) => handleFileChange(e, 'orCr')}
                  onRemoveFile={(file) => removeFile('orCr', file)}
               />
               <UploadCard
                  label="Driver's License"
                  documentLabel="Upload Driver's License"
                  files={formData.files.driverLicense}
                  onFileChange={(e) => handleFileChange(e, 'driverLicense')}
                  onRemoveFile={(file) => removeFile('driverLicense', file)}
               />
               <UploadCard
                  label="Special Power of Attorney"
                  documentLabel={<>Upload Special Power of Attorney <strong>(If Applicable)</strong></>}
                  files={formData.files.specialPowerOfAttorney}
                  onFileChange={(e) => handleFileChange(e, 'specialPowerOfAttorney')}
                  onRemoveFile={(file) => removeFile('specialPowerOfAttorney', file)}
               />
            </div>
         )
      },
      {
         title: 'Review Your Application',
         content: (
            <div className="space-y-6 h-[500px] overflow-y-auto custom-scrollbar">
               <div className="review-step-container custom-scrollbar flex-grow overflow-auto">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                     {Object.entries(formData)
                        .filter(([key]) => key !== 'status' && key !== 'dateOfSubmission' && key !== 'files')
                        .map(([key, value]) => (
                           <div key={key} className="bg-background border rounded-lg p-3">
                              <h4 className="font-semibold text-green-600 dark:text-green-500 mb-1 text-sm">
                                 {formatLabel(key)}
                              </h4>
                              <p className="text-foreground text-sm">
                                 {formatReviewValue(key, value)}
                              </p>
                           </div>
                        ))}
                  </div>

                  <div className="bg-background border rounded-lg p-3 mt-4">
                     <h4 className="font-semibold text-green-600 dark:text-green-500 mb-2 text-sm">
                        Uploaded Files
                     </h4>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {Object.entries(formData.files).map(([docType, files]) => (
                           <div key={docType} className="border-b dark:border-gray-700 pb-2">
                              <h5 className="font-medium text-foreground mb-1 text-xs">
                                 {formatLabel(docType)}
                              </h5>
                              {files.length > 0 ? (
                                 <ul className="list-disc list-inside">
                                    {files.map((file, index) => (
                                       <li key={index} className="text-xs text-muted-foreground">
                                          {file.name}
                                       </li>
                                    ))}
                                 </ul>
                              ) : (
                                 <p className="text-xs text-muted-foreground">No files uploaded</p>
                              )}
                           </div>
                        ))}
                     </div>
                  </div>
               </div>
            </div>
         )
      }
   ];

   useEffect(() => {
      localStorage.setItem('covFormStep', currentStep.toString());
   }, [currentStep]);

   useEffect(() => {
      localStorage.setItem('covFormData', JSON.stringify(formData));
   }, [formData]);

   useEffect(() => {
      const handleBeforeUnload = (e) => {
         // If it's a refresh, persist the data
         if (e.persisted) {
            return;
         }
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
         window.removeEventListener('beforeunload', handleBeforeUnload);

         // Get the navigation type
         const perfEntries = performance.getEntriesByType('navigation');
         const navType = perfEntries.length > 0 ? perfEntries[0].type : '';

         // Only clear if it's not a refresh/reload
         if (navType !== 'reload') {
            // Clear form data from localStorage when component unmounts (not on refresh)
            localStorage.removeItem('covFormStep');
            localStorage.removeItem('covFormData');
            // Reset form data to initial state
            setFormData({
               applicationType: 'Certificate of Verification',
               name: '',
               address: '',
               cellphone: '',
               purpose: '',
               driverName: '',
               driverAddress: '',
               driverLicenseNumber: '',
               vehiclePlateNumber: '',
               originAddress: '',
               destinationAddress: '',
               files: {
                  letterOfIntent: [],
                  tallySheet: [],
                  forestCertification: [],
                  orCr: [],
                  driverLicense: [],
                  specialPowerOfAttorney: []
               },
               dateOfSubmission: '',
               status: ''
            });
            // Reset current step
            setCurrentStep(0);
         }
      };
   }, []);

   return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white dark:from-gray-900 dark:to-gray-800 transition-colors duration-300 flex flex-col justify-between pt-[83px]">
         <div className="container mx-auto px-4 flex-grow">
            <h1 className="text-2xl font-[700] text-green-800 dark:text-green-500 mb-6 text-center">
               Certificate of Verification Application
            </h1>
            <div className="max-w-2xl mx-auto mb-6">
               <FormStepIndicator currentStep={currentStep + 1} formType="COV" />
            </div>
            <Card className="max-w-2xl mx-auto shadow-lg bg-background">
               <CardHeader>
                  <CardTitle className="text-foreground">{steps[currentStep].title}</CardTitle>
               </CardHeader>
               <CardContent>
                  <form onSubmit={handleSubmit}>
                     {steps[currentStep].content}
                  </form>
               </CardContent>
               <CardFooter className="mt-14 flex flex-col sm:flex-row justify-between items-center gap-4">
                  <div className="w-full sm:w-auto">
                     {currentStep > 0 && (
                        <Button
                           type="button"
                           variant="outline"
                           onClick={handlePrevStep}
                           className="w-full sm:w-auto"
                           disabled={isSubmitting || isSavingDraft}
                        >
                           Previous
                        </Button>
                     )}
                  </div>
                  <div className="w-full sm:w-auto flex flex-col sm:flex-row gap-2">
                     {currentStep < steps.length - 1 ? (
                        <Button
                           type="button"
                           onClick={handleNextStep}
                           className="w-full sm:w-auto bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                           disabled={isSubmitting || isSavingDraft}
                        >
                           Next
                        </Button>
                     ) : (
                        <>
                           <Button
                              type="button"
                              variant="outline"
                              onClick={handleSaveAsDraft}
                              className="w-full sm:w-auto"
                              disabled={isSubmitting || isSavingDraft}
                           >
                              {isSavingDraft ? (
                                 <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Saving...
                                 </>
                              ) : (
                                 'Save as Draft'
                              )}
                           </Button>
                           <Button
                              type="submit"
                              onClick={handleSubmit}
                              className="w-full sm:w-auto bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                              disabled={isSubmitting || isSavingDraft}
                           >
                              {isSubmitting ? (
                                 <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Submitting...
                                 </>
                              ) : (
                                 'Submit Application'
                              )}
                           </Button>
                        </>
                     )}
                  </div>
               </CardFooter>
            </Card>
         </div>
         <Modal
            isOpen={modalOpen}
            title={modalContent.title}
            message={modalContent.message}
            onClose={() => setModalOpen(false)}
            onHome={() => navigate('/home')}
            onApplications={() => navigate('/applicationsStatus')}
         />
         <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
            <AlertDialogContent>
               <AlertDialogHeader>
                  <AlertDialogTitle>Submit Application</AlertDialogTitle>
                  <AlertDialogDescription>
                     Are you sure you want to submit this application? Please review all details carefully before proceeding.
                  </AlertDialogDescription>
               </AlertDialogHeader>
               <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                     onClick={handleConfirmSubmit}
                     className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                  >
                     {isSubmitting ? (
                        <>
                           <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                           Submitting...
                        </>
                     ) : (
                        'Submit'
                     )}
                  </AlertDialogAction>
               </AlertDialogFooter>
            </AlertDialogContent>
         </AlertDialog>

         <AlertDialog open={showDraftDialog} onOpenChange={setShowDraftDialog}>
            <AlertDialogContent>
               <AlertDialogHeader>
                  <AlertDialogTitle>Save as Draft</AlertDialogTitle>
                  <AlertDialogDescription>
                     Do you want to save your current progress as a draft? You can continue editing it later.
                  </AlertDialogDescription>
               </AlertDialogHeader>
               <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                     onClick={handleConfirmSaveAsDraft}
                     className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                  >
                     {isSavingDraft ? (
                        <>
                           <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                           Saving...
                        </>
                     ) : (
                        'Save Draft'
                     )}
                  </AlertDialogAction>
               </AlertDialogFooter>
            </AlertDialogContent>
         </AlertDialog>
      </div>
   );
};

export default COVForm;